/* OSYN FX — micro-interactions « whaou » du design system (vanilla, compositor).
   Tilt 3D réactif · count-up · sweep des étoiles. Préfixe data-osyn-* (zéro
   collision Concept). Gated : tilt seulement fine-pointer && !reduced-motion.
   Idempotent : marque data-osyn-fx, ré-exécute sur shopify:section:load. */
(() => {
  'use strict';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const once = (el) => { if (el.dataset.osynFx) return false; el.dataset.osynFx = '1'; return true; };
  const lerp = (a, b, t) => a + (b - a) * t;
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  /* ---- Reveal de secours : si osyn-ui.js absent, on révèle quand même ----- */
  const io = new IntersectionObserver((ents) => {
    ents.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
  }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });

  /* ---- P3 · Tilt 3D réactif (fine-pointer && !RM) ------------------------- */
  const active = new Set();
  let rafTilt = 0;
  function tiltFrame() {
    active.forEach((st) => {
      st.rx = lerp(st.rx, st.trx, 0.14);
      st.ry = lerp(st.ry, st.try, 0.14);
      st.el.style.setProperty('--osyn-tilt-rx', st.rx.toFixed(2) + 'deg');
      st.el.style.setProperty('--osyn-tilt-ry', st.ry.toFixed(2) + 'deg');
    });
    rafTilt = active.size ? requestAnimationFrame(tiltFrame) : 0;
  }
  function initTilt(root) {
    if (!fine || reduce) return;
    $$('[data-osyn-tilt]', root).forEach((el) => {
      if (!once(el)) return;
      const inner = el.querySelector('.osyn-tilt__inner');
      if (!inner) return;
      const glare = el.querySelector('.osyn-tilt__glare');
      const amp = parseFloat(getComputedStyle(el).getPropertyValue('--osyn-tilt-amp')) || 6;
      const st = { el, rx: 0, ry: 0, trx: 0, try: 0 };
      el.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        el.classList.add('is-tilting');
        active.add(st);
        if (!rafTilt) rafTilt = requestAnimationFrame(tiltFrame);
      });
      el.addEventListener('pointermove', (e) => {
        if (e.pointerType !== 'mouse') return;
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width;
        const py = (e.clientY - r.top) / r.height;
        st.try = (px - 0.5) * 2 * amp;
        st.trx = (py - 0.5) * -2 * amp;
        if (glare) { glare.style.setProperty('--osyn-tilt-mx', (px * 100).toFixed(1) + '%'); glare.style.setProperty('--osyn-tilt-my', (py * 100).toFixed(1) + '%'); }
      });
      el.addEventListener('pointerleave', () => {
        active.delete(st);
        st.trx = st.try = 0;
        el.classList.remove('is-tilting');               /* transition ressort → retour à 0 */
        el.style.setProperty('--osyn-tilt-rx', '0deg');
        el.style.setProperty('--osyn-tilt-ry', '0deg');
      });
    });
  }

  /* ---- P6 · Count-up ------------------------------------------------------ */
  function runCount(el) {
    const target = parseFloat(el.dataset.osynCountup);
    if (isNaN(target)) return;
    const dec = parseInt(el.dataset.osynDecimals || '0', 10);
    const sep = el.dataset.osynSep || '.';
    const pre = el.dataset.osynPrefix || '';
    const suf = el.dataset.osynSuffix || '';
    const fmt = (v) => pre + v.toFixed(dec).replace('.', sep) + suf;
    if (reduce) { el.textContent = fmt(target); return; }
    const dur = 1200; let start = 0;
    const step = (ts) => {
      if (!start) start = ts;
      const t = Math.min(1, (ts - start) / dur);
      el.textContent = fmt(target * easeOut(t));
      if (t < 1) requestAnimationFrame(step); else el.textContent = fmt(target);
    };
    requestAnimationFrame(step);
  }
  function initCount(root) {
    $$('[data-osyn-countup]', root).forEach((el) => {
      if (!once(el)) return;
      const obs = new IntersectionObserver((ents, o) => {
        ents.forEach((e) => { if (e.isIntersecting) { runCount(el); o.disconnect(); } });
      }, { threshold: 0.4 });
      obs.observe(el);
    });
  }

  /* ---- P4 · Sweep des étoiles (remplissage 0→note au reveal) -------------- */
  function initStars(root) {
    $$('[data-osyn-stars-sweep]', root).forEach((el) => {
      if (!once(el)) return;
      if (reduce) return;                                /* RM : remplissage déjà posé */
      const fill = el.querySelector('.osyn-stars__layer--fill');
      if (!fill) return;
      const target = el.style.getPropertyValue('--osyn-stars-fill') || '100%';
      fill.style.transition = 'none';
      el.style.setProperty('--osyn-stars-fill', '0%');
      // force reflow puis ré-active la transition
      void el.offsetWidth;
      fill.style.transition = '';
      const obs = new IntersectionObserver((ents, o) => {
        ents.forEach((e) => { if (e.isIntersecting) { el.style.setProperty('--osyn-stars-fill', target); o.disconnect(); } });
      }, { threshold: 0.4 });
      obs.observe(el);
    });
  }

  /* ---- Galerie front/rear · cross-fade + points (le fondu est géré en CSS
         via .is-swapped ; ici : état, aria, préchargement). Reste fonctionnel
         en reduced-motion (seule la transition CSS est neutralisée). --------- */
  function initSwap(root) {
    $$('[data-osyn-swap]', root).forEach((group) => {
      if (!once(group)) return;
      const advance = group.querySelector('[data-osyn-swap-advance]');
      const views = advance ? $$('img', advance) : $$('img', group);
      const dots = $$('[data-osyn-swap-dot]', group);
      if (views.length < 2) return;
      let idx = 0;
      const apply = () => {
        group.classList.toggle('is-swapped', idx === 1);
        views.forEach((v, i) => v.setAttribute('aria-hidden', i === idx ? 'false' : 'true'));
        dots.forEach((d, i) => {
          d.classList.toggle('is-active', i === idx);
          d.setAttribute('aria-pressed', i === idx ? 'true' : 'false');
        });
      };
      const set = (i) => { idx = i === 1 ? 1 : 0; apply(); };
      if (advance) advance.addEventListener('click', () => set(idx === 0 ? 1 : 0));
      dots.forEach((d, i) => d.addEventListener('click', () => set(i)));
      // précharge la face arrière au 1er survol/focus → pas de flash au fondu
      const rear = views[1];
      const preload = () => { if (rear && rear.decode) rear.decode().catch(() => {}); };
      group.addEventListener('pointerenter', preload, { once: true });
      group.addEventListener('focusin', preload, { once: true });
      apply();
    });
  }

  function initFx(root) {
    const r = root || document;
    initTilt(r);
    initCount(r);
    initStars(r);
    initSwap(r);
  }
  if (document.readyState !== 'loading') initFx();
  else document.addEventListener('DOMContentLoaded', () => initFx());
  document.addEventListener('shopify:section:load', (e) => initFx(e.target));
  window.osynFx = initFx;
})();
